"use client";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Intro from "@/ui/Intro";
import Image from "next/image";

const extractText = (richText) => {
    if (!richText) return "";
    if (typeof richText === "string") return richText.trim();
    if (Array.isArray(richText)) {
        return richText.map(block => extractText(block)).join(" ").trim();
    }
    if (richText.children && Array.isArray(richText.children)) {
        return richText.children
            .map(child => {
                if (child.type === "text" && child.text) return child.text;
                return extractText(child);
            })
            .join("")
            .trim();
    }
    return "";
};

const getImageUrl = (imageData) => {
    if (!imageData) return "";
    if (imageData.url) return imageData.url;
    if (imageData.data?.[0]?.attributes?.url) return imageData.data[0].attributes.url;
    if (imageData.data?.[0]?.url) return imageData.data[0].url;
    if (imageData.formats?.thumbnail?.url) return imageData.formats.thumbnail.url;
    if (imageData.formats?.medium?.url) return imageData.formats.medium.url;
    if (imageData.formats?.large?.url) return imageData.formats.large.url;
    if (imageData.formats?.small?.url) return imageData.formats.small.url;

    if (imageData.attributes?.url) return imageData.attributes.url;
    return "";
};

const Ourmentors = ({ mentors = null }) => {
    let mentorsArray = [];
    if (Array.isArray(mentors)) {
        mentorsArray = mentors;
    } else if (mentors?.mentordetails) {
        mentorsArray = Array.isArray(mentors.mentordetails) ? mentors.mentordetails : [mentors.mentordetails];
    }
    if (!mentorsArray?.length) {
        console.log("No mentors found");
        return null;
    }

    const normalizedMentors = mentorsArray.map((mData, idx) => {
        const m = mData.attributes || mData;
        const imageUrl = getImageUrl(m.Mentorimage);
        return {
            id: mData.id ?? idx,
            name: extractText(m.MentorName),
            description: extractText(m.Mentordetails),
            image: imageUrl
        };
    }).filter(m => m.name);
    if (!normalizedMentors.length) return null;

    return (
        <section className="w-full text-black space-y-2 ">
            <div className="page-container py-[12px] md:py-[14px] mx-auto">
                <div className="flex items-center justify-between gap-4 mb-8">
                    <Intro
                        title="Our Esteemed Mentors"
                        description="A dedicated team of experienced educators and industry specialists committed to nurturing students with expert guidance, practical insight, and strong academic and professional mentorship."
                    />
                    <div className="hidden md:flex items-center gap-3 shrink-0">
                        <button className="mentor-prev inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors" type="button">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button className="mentor-next inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors" type="button">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <Swiper
                    modules={[Navigation]}
                    navigation={{
                        prevEl: ".mentor-prev",
                        nextEl: ".mentor-next",
                    }}
                    spaceBetween={24}
                    slidesPerView={1}
                    breakpoints={{
                        640: { slidesPerView: 2, spaceBetween: 24 },
                        1024: { slidesPerView: 4, spaceBetween: 24 },
                    }}
                    className="!overflow-hidden"
                >
                    {normalizedMentors.map((mentor) => (
                        <SwiperSlide key={mentor.id}>
                            <article className="relative bg-white overflow-hidden h-full">
                                <div className="h-64 md:h-72 w-full overflow-hidden bg-gray-100">
                                    {mentor.image ? (
                                        <Image
                                            src={mentor.image}
                                            alt={mentor.name}
                                            width={400}
                                            height={300}
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50 text-sky-500 text-sm p-4">
                                            No Image
                                        </div>
                                    )}
                                </div>
                                <div className="px-4 py-6 border border-gray-200 bg-gray-50">
                                    <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-2" style={{ fontFamily: "saans-bold" }}>
                                        {mentor.name}
                                    </h3>
                                    {mentor.description && (
                                        <p className="text-sm leading-relaxed text-gray-600 line-clamp-3">
                                            {mentor.description}
                                        </p>
                                    )}
                                </div>
                            </article>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
};

export default Ourmentors;
